import React from "react";
import moment from "moment-timezone";
import {
  InformationCircleIcon,
  ArrowPathIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import {
  AnnouncementI,
  AnnouncementTypeEnum,
  AnnouncementPriorityEnum,
} from "../../../types";
import { pluralize } from "../../../utils/pluralize";

interface AnnouncementCardProps {
  announcement: AnnouncementI;
  isSelected: boolean;
  onClick: () => void;
}

const AnnouncementCard: React.FC<AnnouncementCardProps> = ({
  announcement,
  isSelected,
  onClick,
}) => {
  const getStatusInfo = () => {
    const now = moment();
    const startsAt = moment(announcement.startsAt);
    const expiresAt = announcement.expiresAt
      ? moment(announcement.expiresAt)
      : null;

    if (now.isBefore(startsAt)) {
      return { label: "Upcoming", color: "bg-blue-100 text-blue-800" };
    }
    if (expiresAt && now.isAfter(expiresAt)) {
      return { label: "Expired", color: "bg-gray-100 text-gray-800" };
    }
    return { label: "Active", color: "bg-green-100 text-green-800" };
  };

  const getPriorityColor = () => {
    const colors = {
      [AnnouncementPriorityEnum.LOW]: "bg-gray-100 text-gray-800",
      [AnnouncementPriorityEnum.MEDIUM]: "bg-blue-100 text-blue-800",
      [AnnouncementPriorityEnum.HIGH]: "bg-orange-100 text-orange-800",
      [AnnouncementPriorityEnum.CRITICAL]: "bg-red-100 text-red-800",
    };
    return (
      colors[announcement.priority] || colors[AnnouncementPriorityEnum.MEDIUM]
    );
  };

  const getTypeIcon = () => {
    const iconClass = "h-5 w-5";
    const icons = {
      [AnnouncementTypeEnum.INFO]: (
        <InformationCircleIcon className={iconClass} />
      ),
      [AnnouncementTypeEnum.UPDATE]: <ArrowPathIcon className={iconClass} />,
      [AnnouncementTypeEnum.FEATURE]: <SparklesIcon className={iconClass} />,
    };
    return icons[announcement.type] || icons[AnnouncementTypeEnum.INFO];
  };

  const status = getStatusInfo();

  return (
    <div
      onClick={onClick}
      className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
        isSelected
          ? "border-blue-500 bg-blue-50"
          : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="flex-shrink-0">{getTypeIcon()}</div>
          <h3 className="font-semibold text-gray-900 line-clamp-1 text-sm">
            {announcement.title}
          </h3>
        </div>
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ml-2 ${status.color}`}
        >
          {status.label}
        </span>
      </div>

      <p className="text-sm text-gray-600 line-clamp-2 mb-3">
        {announcement.message}
      </p>

      <div className="flex flex-wrap gap-2 text-xs mb-3">
        <span className={`px-2 py-1 rounded font-medium ${getPriorityColor()}`}>
          {announcement.priority.charAt(0).toUpperCase() +
            announcement.priority.slice(1)}
        </span>
        <span className="px-2 py-1 rounded bg-purple-100 text-purple-800 font-medium">
          {announcement.type.charAt(0).toUpperCase() +
            announcement.type.slice(1)}
        </span>
        {announcement.actions && announcement.actions.length > 0 && (
          <span className="px-2 py-1 rounded bg-indigo-100 text-indigo-800 font-medium">
            {pluralize("Action", announcement.actions.length, true)}
          </span>
        )}
      </div>

      <div className="text-xs text-gray-500 space-y-1">
        <div className="flex items-center gap-1">
          <span className="font-medium">Starts:</span>
          <span>
            {moment(announcement.startsAt).format("MMM D, YYYY h:mm A")}
          </span>
        </div>
        {announcement.expiresAt && (
          <div className="flex items-center gap-1">
            <span className="font-medium">Expires:</span>
            <span>
              {moment(announcement.expiresAt).format("MMM D, YYYY h:mm A")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementCard;
